import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getSignalRConfig } from '@/common/infrastructure/config/signalRConfig';
import { SignalRService, type SignalRConnectionState } from '@/common/infrastructure/services/SignalRService';

import { UM_CONTEXT_KEYS } from './queryKeys';

/**
 * ClientHub events that should refresh the UM notifications bell.
 * - `OnUmNotification` — unified push (TROC-201)
 * - `ReviewPermissionsAssigned` — IT apply → reviewer/manager (TROC-150; still emitted by some BE builds)
 * - `RequestCancelled` — requester cancel → reviewer (TROC-155)
 */
export const UM_NOTIFICATION_SIGNALR_EVENTS = ['OnUmNotification', 'ReviewPermissionsAssigned', 'RequestCancelled'] as const;

const isSignalRDisabled = import.meta.env.VITE_DISABLE_SIGNALR === 'true';

function resolveSignalRService(): SignalRService | null {
  try {
    return SignalRService.getInstance(getSignalRConfig());
  } catch {
    return null;
  }
}

/**
 * Subscribes to UM notification hub events on the shared ClientHub connection
 * opened by `GpsSignalRProvider`. Does not acquire or connect — avoids racing connect().
 */
export function useUmNotificationsRealtime(enabled: boolean): void {
  const queryClient = useQueryClient();
  const shouldSubscribe = enabled && !isSignalRDisabled;

  useEffect(() => {
    if (!shouldSubscribe) {
      return;
    }

    const signalRService = resolveSignalRService();
    if (!signalRService) {
      return;
    }

    const refreshNotifications = () => {
      void queryClient.invalidateQueries({ queryKey: UM_CONTEXT_KEYS.notifications() });
      void queryClient.refetchQueries({ queryKey: UM_CONTEXT_KEYS.notifications() });
    };

    const subscribeAll = () => {
      if (!signalRService.isConnected()) {
        return;
      }
      for (const event of UM_NOTIFICATION_SIGNALR_EVENTS) {
        signalRService.on(event, refreshNotifications);
      }
    };

    const unsubscribeAll = () => {
      for (const event of UM_NOTIFICATION_SIGNALR_EVENTS) {
        signalRService.off(event, refreshNotifications);
      }
    };

    const onConnectionStateChange = (state: SignalRConnectionState) => {
      if (!state.isConnected) {
        return;
      }
      unsubscribeAll();
      subscribeAll();
    };

    signalRService.onConnectionStateChange(onConnectionStateChange);

    return () => {
      signalRService.offConnectionStateChange(onConnectionStateChange);
      unsubscribeAll();
    };
  }, [shouldSubscribe, queryClient]);
}
